import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { exigir } from '../../nucleo/auth/configuracao';
import { Button } from '../../ui/base/button';
import { LayoutAutenticacao } from './LayoutAutenticacao';

interface Estado {
  titulo: string;
  descricao: string;
  fase: string;
  identidadeMascarada: string | null;
  validade: string | null;
  requisitosCredencial: string[];
  verificacao: { confirmada: boolean; mensagem: string };
  submissao: { permitida: boolean; emAndamento: boolean };
  erro: string | null;
  confirmacao: string | null;
  proximoDestino: 'PROVEDOR_IDENTIDADE' | 'MFA' | 'LOGIN' | null;
  comandos: string[];
  navegacao: Array<{ rotulo: string; caminho: string }>;
}
interface Envelope {
  contract_version: string;
  screen: string;
  event: { verb: string };
  state: Estado;
}
function enderecoDoProvedor(acao: string) {
  const url = new URL(acao);
  const publico = new URL(exigir('VITE_KEYCLOAK_URL', import.meta.env.VITE_KEYCLOAK_URL));
  url.protocol = publico.protocol;
  url.host = publico.host;
  return url.toString();
}
async function verbo(nome: string, token?: string) {
  const api = exigir('VITE_API_URL', import.meta.env.VITE_API_URL);
  const r = await fetch(`${api}/bff/v1/primeiro-acesso/verbs/${nome}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(token ? { token } : {}),
  });
  if (!r.ok) throw new Error();
  return r.json() as Promise<Envelope>;
}

const MENSAGEM_DA_CONSULTA_QUE_FALHOU =
  'Não foi possível consultar o convite. Tente de novo em instantes ou volte para entrar.';
const MENSAGEM_DA_ACAO_QUE_FALHOU =
  'Não foi possível concluir agora. Tente de novo em instantes; se continuar, procure a DTP para receber um novo convite.';

/**
 * `/primeiro-acesso?acao=…` — a tela a que o e-mail de convite leva.
 *
 * O título e a descrição da página são os da casca (`LayoutAutenticacao`), que
 * já traz o `<main>` e o `<h1>`: a tela só os alimenta com o estado do convite.
 * Falha da consulta e falha da ação dizem o que fazer (regra 8) e nunca deixam
 * a pessoa sem saída: "Voltar para entrar" está sempre ao alcance.
 */
export function PrimeiroAcessoPage() {
  const [busca] = useSearchParams();
  const acao = busca.get('acao') ?? '';
  const [estado, setEstado] = useState<Estado | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [falhaDaAcao, setFalhaDaAcao] = useState(false);
  const [tentativa, setTentativa] = useState(0);
  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    verbo('consultar_estado', acao || undefined)
      .then((e) => {
        if (!cancelado) setEstado(e.state);
      })
      .catch(() => {
        if (!cancelado) setEstado(null);
      })
      .finally(() => {
        if (!cancelado) setCarregando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [acao, tentativa]);
  const executar = async (nome: string) => {
    setCarregando(true);
    setFalhaDaAcao(false);
    try {
      const e = await verbo(nome, acao || undefined);
      setEstado(e.state);
    } catch {
      // O estado anterior continua na tela, e a pessoa pode tentar de novo.
      setFalhaDaAcao(true);
    } finally {
      setCarregando(false);
    }
  };
  const consultaFalhou = !carregando && !estado;
  return (
    <LayoutAutenticacao
      titulo={estado?.titulo ?? 'Primeiro acesso'}
      descricao={
        estado?.descricao ??
        (consultaFalhou ? 'Não conseguimos verificar o convite.' : 'Verificando o convite…')
      }
    >
      <div className="space-y-6" aria-busy={carregando}>
        {carregando && (
          <div className="flex gap-2" role="status">
            <Loader2 className="size-5 animate-spin" />
            Carregando estado seguro…
          </div>
        )}
        {consultaFalhou && (
          <>
            <div role="alert" className="flex gap-2 text-destructive">
              <AlertCircle className="size-5 shrink-0" aria-hidden="true" />
              {MENSAGEM_DA_CONSULTA_QUE_FALHOU}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button onClick={() => setTentativa((n) => n + 1)}>Tentar de novo</Button>
              <Button variant="ghost" onClick={() => window.location.assign('/login')}>
                Voltar para entrar
              </Button>
            </div>
          </>
        )}
        {estado && (
          <>
            <div className="rounded-md bg-muted p-4">
              <p className="font-medium">{estado.identidadeMascarada ?? 'Identidade protegida'}</p>
              <p className="text-sm text-muted-foreground">{estado.verificacao.mensagem}</p>
              {estado.validade && (
                <p className="mt-2 text-sm">
                  Válido até {new Date(estado.validade).toLocaleString('pt-BR')}
                </p>
              )}
            </div>
            {estado.requisitosCredencial.length > 0 && (
              <section>
                <h2 className="font-medium">Requisitos da credencial</h2>
                <ul className="list-disc pl-5 text-sm text-muted-foreground">
                  {estado.requisitosCredencial.map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </section>
            )}
            {estado.erro && (
              <div role="alert" className="flex gap-2 text-destructive">
                <AlertCircle className="size-5" />
                {estado.erro}
              </div>
            )}
            {falhaDaAcao && (
              <div role="alert" className="flex gap-2 text-destructive">
                <AlertCircle className="size-5 shrink-0" aria-hidden="true" />
                {MENSAGEM_DA_ACAO_QUE_FALHOU}
              </div>
            )}
            {estado.confirmacao && (
              <div role="status" className="flex gap-2 text-primary">
                <CheckCircle2 className="size-5" />
                {estado.confirmacao}
              </div>
            )}
            <div className="flex flex-col gap-3 sm:flex-row">
              {estado.proximoDestino === 'PROVEDOR_IDENTIDADE' && acao && (
                <Button
                  onClick={() => {
                    window.location.assign(enderecoDoProvedor(acao));
                  }}
                >
                  Definir credencial no provedor
                </Button>
              )}
              {acao && (
                <Button
                  variant="outline"
                  onClick={() => executar('definir_credencial')}
                  disabled={carregando}
                >
                  Confirmar conclusão
                </Button>
              )}
              {estado.comandos.includes('solicitar_reemissao') && (
                <Button onClick={() => executar('solicitar_reemissao')} disabled={carregando}>
                  Solicitar orientação de reemissão
                </Button>
              )}
              <Button variant="ghost" onClick={() => window.location.assign('/login')}>
                Voltar para entrar
              </Button>
            </div>
          </>
        )}
      </div>
    </LayoutAutenticacao>
  );
}
